/** @jsx jsx */

import { ChangeEventHandler } from 'react';

import {
  Alert,
  AlertDescription,
  Dropdown,
  Field,
  Grid,
  Surface,
  Text,
  TextInput,
} from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import Link from 'app/common/components/Link';
import {
  BASE_APP_URL,
  PLATFORM_MARKETING_BASE_URL,
} from 'app/developer-v3/constants';

import type { SemVer } from 'app/developer-v3/types/version';

const Styles = {
  root: css`
    padding: 20px;
  `,
  label: css`
    vertical-align: top;
  `,
};
type Props = {
  appVersion: SemVer;
  category: string;
  email: string;
  hasAcceptedTerms: boolean;
  isStaff: boolean;
  onChangeEmail: (ChangeEventHandler) => void;
  onChangeFilter: (item) => void;
  period?: {
    end: string;
    start: string;
  };
};

const categoryDropdownItems = appVersion => [
  {
    value: 'all',
    label: 'all the events',
  },
  { value: '2xx', label: 'only the 2xx requests' },
  { value: '3xx', label: 'only the 3xx requests' },
  { value: '4xx', label: 'only the 4xx requests' },
  { value: '5xx', label: 'only the 5xx requests' },
  { value: 'hooks', label: 'only the inbound webhooks' },
  { value: 'errors', label: 'only the errors' },
  { value: 'bundle', label: 'only the bundle logs' },
  appVersion && { value: 'console', label: 'only the console logs' },
];

function EventFilter({
  appVersion,
  category,
  email,
  hasAcceptedTerms,
  isStaff,
  onChangeEmail,
  onChangeFilter,
  period,
}: Props) {
  const dropdownItems = categoryDropdownItems(appVersion);

  return (
    <Surface borderColor="GrayWarm4">
      <div css={Styles.root}>
        <Grid columns={1} gap={10}>
          {!isStaff && !hasAcceptedTerms && (
            <Alert title="Zapier Platform Agreement">
              <AlertDescription>
                <Text>
                  To view logs for all users of your integration, you'll need to
                  sign the updated{' '}
                  <Link
                    href={`${PLATFORM_MARKETING_BASE_URL}/tos`}
                    target="_blank"
                  >
                    Zapier Platform Agreement
                  </Link>{' '}
                  via the <Link href={`${BASE_APP_URL}/`}>landing page</Link> of
                  the Developer Platform.
                </Text>
              </AlertDescription>
            </Alert>
          )}
          <Grid alignItems="center" columns="auto 1fr" gap={5}>
            <Text tag="h3" type="Body4">
              Event filters
            </Text>
          </Grid>
          <Grid alignItems="center" columns="auto auto" gap={25}>
            <Field
              label="From"
              renderHelpText={() => 'Selected period started from'}
              renderInput={() => (
                <TextInput
                  aria-label="From"
                  isDisabled={true}
                  name="from"
                  value={
                    period?.start.replace('T', ' ').replace('+00:00', '') ?? ''
                  }
                />
              )}
            />
            <Field
              label="To"
              renderHelpText={() => 'Selected period ended at'}
              renderInput={() => (
                <TextInput
                  aria-label="To"
                  isDisabled={true}
                  name="to"
                  value={
                    period?.end.replace('T', ' ').replace('+00:00', '') ?? ''
                  }
                />
              )}
            />
            <Field
              label="Category"
              renderHelpText={() => period && 'What type of events?'}
              renderInput={() => (
                <Dropdown
                  aria-label="Category"
                  initialSelectedItem={dropdownItems[0]}
                  isDisabled={!period}
                  items={dropdownItems}
                  onChange={onChangeFilter}
                  placeholder={dropdownItems[0].label}
                  value={category}
                />
              )}
            />
            <Field
              label="User"
              renderHelpText={() =>
                period &&
                "Which user's events? " +
                  `(Defaults to ${
                    isStaff || hasAcceptedTerms ? 'all users' : 'you'
                  }.)`
              }
              renderInput={() => (
                <TextInput
                  aria-label="Email"
                  isDisabled={!period}
                  name="other-email"
                  onBlur={onChangeEmail}
                  onChange={onChangeEmail}
                  placeholder="user@example.com"
                  value={email}
                />
              )}
            />
          </Grid>
        </Grid>
      </div>
    </Surface>
  );
}

export default EventFilter;
